import { useState, useEffect } from 'react';
import { Clock, CheckCircle, XCircle, MapPin, Calendar } from 'lucide-react';
import { getBrandReservations, cancelReservationRequest } from '../lib/api';
import { MachineReservation } from '../types/map';
import { useToast } from '../components/ui/use-toast';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';

type TabType = 'all' | 'pending' | 'approved' | 'denied';

export default function BrandReservations() {
  const [reservations, setReservations] = useState<MachineReservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const { toast } = useToast();

  useEffect(() => {
    loadReservations();
  }, []);

  const loadReservations = async () => {
    try {
      setLoading(true);
      const data = await getBrandReservations();
      setReservations(data);
    } catch (error) {
      console.error('Error loading reservations:', error);
      toast({
        title: 'Error',
        description: 'Failed to load reservations',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequest = async (id: string) => {
    if (!window.confirm('Are you sure you want to cancel this reservation request?')) {
      return;
    }

    try {
      await cancelReservationRequest(id);
      toast({
        title: 'Success',
        description: 'Reservation request cancelled',
      });
      loadReservations();
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Error cancelling request:', error);
      toast({
        title: 'Error',
        description: 'Failed to cancel request',
        variant: 'destructive',
      });
    }
  };

  const handleBulkCancel = async () => {
    if (selectedIds.size === 0) return;
    
    if (!window.confirm(`Are you sure you want to cancel ${selectedIds.size} reservation request(s)?`)) {
      return;
    }

    try {
      const promises = Array.from(selectedIds).map(id => cancelReservationRequest(id));
      await Promise.all(promises);
      toast({
        title: 'Success',
        description: `${selectedIds.size} reservation request(s) cancelled`,
      });
      loadReservations();
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Error cancelling requests:', error);
      toast({
        title: 'Error',
        description: 'Failed to cancel some requests',
        variant: 'destructive',
      });
    }
  };

  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    const pendingReservations = filteredReservations.filter(r => r.status === 'pending');
    setSelectedIds(new Set(pendingReservations.map(r => r.id)));
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const formatTenderDetails = (notes: string) => {
    // Try to parse as JSON
    try {
      const data = JSON.parse(notes);
      
      // Check if it's the metadata structure
      if (data.venueSelections || data.retailMediaSelections || data.reportingTier) {
        const parts: string[] = [];
        
        // Venue selections
        if (data.venueSelections && Array.isArray(data.venueSelections)) {
          const venueCount = data.venueSelections.length;
          const totalMachines = data.venueSelections.reduce((sum: number, v: any) => sum + (v.machineCount || 0), 0);
          parts.push(`${venueCount} venue${venueCount !== 1 ? 's' : ''} selected (${totalMachines} machine${totalMachines !== 1 ? 's' : ''})`);
        }
        
        // Retail media selections
        if (data.retailMediaSelections && Object.keys(data.retailMediaSelections).length > 0) {
          const mediaCount = Object.keys(data.retailMediaSelections).length;
          parts.push(`${mediaCount} retail media item${mediaCount !== 1 ? 's' : ''} selected`);
        }
        
        // Reporting tier
        if (data.reportingTier) {
          const tierName = data.reportingTier.charAt(0).toUpperCase() + data.reportingTier.slice(1);
          parts.push(`Reporting: ${tierName} tier`);
          
          if (data.reportingOptions) {
            const options: string[] = [];
            if (data.reportingOptions.machineBracket) {
              options.push(`Machine bracket: ${data.reportingOptions.machineBracket}`);
            }
            if (data.reportingOptions.skuTier) {
              options.push(`SKU tier: ${data.reportingOptions.skuTier}`);
            }
            if (data.reportingOptions.liveReporting) {
              options.push('Live reporting enabled');
            }
            if (options.length > 0) {
              parts.push(`Reporting options: ${options.join(', ')}`);
            }
          }
        }
        
        return parts.length > 0 ? parts.join('\n') : 'Long-term tender request';
      }
      
      // If it's JSON but not our structure, return as-is
      return notes;
    } catch {
      // Not JSON, return as-is (user notes)
      return notes;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
            <Clock className="h-4 w-4" />
            Pending Review
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-4 w-4" />
            Approved
          </span>
        );
      case 'denied':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            <XCircle className="h-4 w-4" />
            Denied
          </span>
        );
      default:
        return null;
    }
  };

  const filteredReservations = reservations.filter(r => {
    if (activeTab === 'all') return true;
    return r.status === activeTab;
  });

  const stats = {
    total: reservations.length,
    pending: reservations.filter(r => r.status === 'pending').length,
    approved: reservations.filter(r => r.status === 'approved').length,
    denied: reservations.filter(r => r.status === 'denied').length,
  };

  return (
    <div className="h-full bg-gray-50">
      <div className="w-full px-2 md:px-4 py-4 md:py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Reservations</h1>
          <p className="text-gray-600 mt-2">
            View and manage your machine reservation requests
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border p-4">
            <div className="text-sm text-gray-600">Total Requests</div>
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          </div>
          <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4">
            <div className="text-sm text-yellow-800">Pending</div>
            <div className="text-2xl font-bold text-yellow-900">{stats.pending}</div>
          </div>
          <div className="bg-green-50 rounded-lg border border-green-200 p-4">
            <div className="text-sm text-green-800">Approved</div>
            <div className="text-2xl font-bold text-green-900">{stats.approved}</div>
          </div>
          <div className="bg-red-50 rounded-lg border border-red-200 p-4">
            <div className="text-sm text-red-800">Denied</div>
            <div className="text-2xl font-bold text-red-900">{stats.denied}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg border mb-6">
          <div className="flex border-b">
            {(['all', 'pending', 'approved', 'denied'] as TabType[]).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-3 text-sm font-medium capitalize transition-colors ${
                  activeTab === tab
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab}
                <span className="ml-2 text-xs bg-gray-100 px-2 py-0.5 rounded-full">
                  {tab === 'all' ? stats.total : stats[tab]}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Bulk Actions */}
        {filteredReservations.some(r => r.status === 'pending') && (
          <div className="bg-white rounded-lg border p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={selectedIds.size === filteredReservations.filter(r => r.status === 'pending').length ? deselectAll : selectAll}
              >
                {selectedIds.size === filteredReservations.filter(r => r.status === 'pending').length && selectedIds.size > 0 ? 'Deselect All' : 'Select All Pending'}
              </Button>
              {selectedIds.size > 0 && (
                <span className="text-sm text-gray-600">
                  {selectedIds.size} selected
                </span>
              )}
            </div>
            {selectedIds.size > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkCancel}
              >
                Cancel Selected ({selectedIds.size})
              </Button>
            )}
          </div>
        )}

        {/* Reservations List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading reservations...</p>
          </div>
        ) : filteredReservations.length === 0 ? (
          <div className="bg-white rounded-lg border p-12 text-center">
            <div className="text-gray-400 mb-4">
              <Calendar className="h-16 w-16 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No {activeTab !== 'all' ? activeTab : ''} reservations
            </h3>
            <p className="text-gray-600">
              {activeTab === 'all' 
                ? "You haven't made any reservation requests yet."
                : `You don't have any ${activeTab} requests.`}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredReservations.map(reservation => (
              <div key={reservation.id} className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  {reservation.status === 'pending' && (
                    <div className="mr-4 pt-1">
                      <Checkbox
                        checked={selectedIds.has(reservation.id)}
                        onCheckedChange={() => toggleSelection(reservation.id)}
                      />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {reservation.Machine?.name || 'Unknown Machine'}
                      </h3>
                      {getStatusBadge(reservation.status)}
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1.5">
                        <MapPin className="h-4 w-4" />
                        <span>
                          {reservation.Machine?.venueType}
                          {reservation.Machine?.venueName && ` • ${reservation.Machine.venueName}`}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-4 w-4" />
                        <span>Requested {reservation.detailedInfo ? formatDateTime(reservation.detailedInfo.requestedAt) : formatDate(reservation.requestedAt)}</span>
                      </div>
                    </div>
                  </div>

                  {reservation.status === 'pending' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCancelRequest(reservation.id)}
                      className="text-red-600 hover:text-red-700 hover:border-red-300"
                    >
                      Cancel Request
                    </Button>
                  )}
                </div>

                {reservation.status === 'approved' && reservation.reservationStart && reservation.reservationEnd && (
                  <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-3">
                    <div className="flex items-center gap-4 text-sm">
                      <div>
                        <span className="text-green-700 font-medium">Start:</span>
                        <span className="text-green-900 ml-2">{formatDate(reservation.reservationStart)}</span>
                      </div>
                      <div>
                        <span className="text-green-700 font-medium">End:</span>
                        <span className="text-green-900 ml-2">{formatDate(reservation.reservationEnd)}</span>
                      </div>
                    </div>
                    {reservation.reviewedAt && (
                      <div className="text-xs text-green-700 mt-1">
                        Approved on {formatDate(reservation.reviewedAt)}
                      </div>
                    )}
                  </div>
                )}

                {reservation.status === 'denied' && reservation.denialReason && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-3">
                    <div className="text-sm font-medium text-red-900 mb-1">Reason for Denial</div>
                    <div className="text-sm text-red-800">{reservation.denialReason}</div>
                    {reservation.reviewedAt && (
                      <div className="text-xs text-red-700 mt-2">
                        Denied on {formatDate(reservation.reviewedAt)}
                      </div>
                    )}
                  </div>
                )}

                {reservation.reservationType && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-3 text-sm">
                    <div className="font-medium text-blue-900 mb-1">Reservation Type</div>
                    <div className="text-blue-800">{reservation.reservationType}</div>
                  </div>
                )}

                {/* Enhanced detailed information for pending requests */}
                {reservation.status === 'pending' && reservation.detailedInfo && (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-md p-4 mb-3">
                    <div className="font-semibold text-blue-900 mb-3 text-lg">Request Details</div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="bg-white rounded-md p-3 border border-blue-100">
                        <div className="text-xs text-gray-500 mb-1">Machines</div>
                        <div className="text-2xl font-bold text-blue-900">{reservation.detailedInfo.machineCount}</div>
                      </div>
                      <div className="bg-white rounded-md p-3 border border-blue-100">
                        <div className="text-xs text-gray-500 mb-1">SKUs Requested</div>
                        <div className="text-2xl font-bold text-blue-900">{reservation.detailedInfo.skuCount}</div>
                      </div>
                      <div className="bg-white rounded-md p-3 border border-blue-100">
                        <div className="text-xs text-gray-500 mb-1">Retail Media Items</div>
                        <div className="text-2xl font-bold text-blue-900">{reservation.detailedInfo.retailMediaCount}</div>
                      </div>
                    </div>

                    {/* SKU Selections */}
                    {reservation.Machine?.requiresTenderProcess && reservation.MachineSKUAllocations && reservation.MachineSKUAllocations.length > 0 && (() => {
                      // Parse planogram config to get category information
                      let planogramConfig: any = null;
                      try {
                        const config = reservation.Machine?.planogramConfig;
                        if (config) {
                          planogramConfig = typeof config === 'string' ? JSON.parse(config) : config;
                        }
                      } catch (e) {
                        console.error('Error parsing planogram config:', e);
                      }

                      // Group SKU allocations by category
                      const categoryCounts: Record<string, number> = {};
                      reservation.MachineSKUAllocations.forEach((alloc) => {
                        if (planogramConfig?.positions) {
                          const position = planogramConfig.positions.find(
                            (p: any) => p.row === alloc.rowIndex && p.col === alloc.columnIndex
                          );
                          const category = position?.category || 'Unknown';
                          categoryCounts[category] = (categoryCounts[category] || 0) + 1;
                        } else {
                          // Fallback if no planogram config
                          categoryCounts['Unknown'] = (categoryCounts['Unknown'] || 0) + 1;
                        }
                      });

                      return (
                        <div className="bg-purple-50 border border-purple-200 rounded-md p-3 mb-3">
                          <div className="font-medium text-purple-900 mb-2">SKU Selections</div>
                          <div className="flex flex-wrap gap-2 mb-2">
                            {Object.entries(categoryCounts).map(([category, count]) => (
                              <span key={category} className="inline-flex items-center px-3 py-1 bg-purple-200 text-purple-900 rounded text-sm font-medium">
                                {count} {category}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Retail Media Selections */}
                    {reservation.detailedInfo.costs.retailMediaCosts.breakdown.length > 0 && (
                      <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3 mb-3">
                        <div className="font-medium text-indigo-900 mb-2">Retail Media Listings</div>
                        <div className="space-y-2">
                          {reservation.detailedInfo.costs.retailMediaCosts.breakdown.map((item) => (
                            <div key={item.itemId} className="bg-white rounded p-2 text-sm">
                              <div className="font-medium text-indigo-900">{item.itemName}</div>
                              <div className="text-xs text-gray-600">
                                {item.machines} machine{item.machines !== 1 ? 's' : ''} × {item.units} unit{item.units !== 1 ? 's' : ''}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Cost Summary */}
                    <div className="bg-green-50 border border-green-200 rounded-md p-4">
                      <div className="font-semibold text-green-900 mb-3">Cost Summary</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-gray-600 mb-1">Monthly Costs</div>
                          {reservation.detailedInfo.costs.skuCosts.monthly > 0 && (
                            <div className="text-sm text-gray-700 mb-1">
                              SKUs: £{reservation.detailedInfo.costs.skuCosts.monthly.toFixed(2)}
                            </div>
                          )}
                          {reservation.detailedInfo.costs.retailMediaCosts.monthly > 0 && (
                            <div className="text-sm text-gray-700 mb-1">
                              Retail Media: £{reservation.detailedInfo.costs.retailMediaCosts.monthly.toFixed(2)}
                            </div>
                          )}
                          <div className="text-lg font-bold text-green-900 mt-2 pt-2 border-t border-green-300">
                            Total Monthly: £{reservation.detailedInfo.costs.total.monthly.toFixed(2)}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-600 mb-1">12 Month Costs</div>
                          {reservation.detailedInfo.costs.skuCosts.yearly > 0 && (
                            <div className="text-sm text-gray-700 mb-1">
                              SKUs: £{reservation.detailedInfo.costs.skuCosts.yearly.toFixed(2)}
                            </div>
                          )}
                          {reservation.detailedInfo.costs.retailMediaCosts.yearly > 0 && (
                            <div className="text-sm text-gray-700 mb-1">
                              Retail Media: £{reservation.detailedInfo.costs.retailMediaCosts.yearly.toFixed(2)}
                            </div>
                          )}
                          <div className="text-lg font-bold text-green-900 mt-2 pt-2 border-t border-green-300">
                            Total 12 Month: £{reservation.detailedInfo.costs.total.yearly.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {reservation.notes && (
                  <div className="bg-gray-50 rounded-md p-3 text-sm">
                    <div className="font-medium text-gray-700 mb-2">
                      {(() => {
                        try {
                          JSON.parse(reservation.notes);
                          return 'Request Details';
                        } catch {
                          return 'Your Notes';
                        }
                      })()}
                    </div>
                    <div className="text-gray-600 whitespace-pre-line">{formatTenderDetails(reservation.notes)}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

